import { FileText } from "lucide-react";
import React, { useCallback, useMemo } from "react";
import type { EvidenceEvent, PdfSourceRef } from "../../../types/evidence";
import { useDopplerUriOpener } from "../../../hooks/useDopplerUriOpener";
import { STATUS_CHIP_CLASSES } from "../../Cabinet/cabinetTheme";
import {
	type DocumentVersionNote,
	lookupVersionNote,
} from "../documentVersionIndex";
import { useDocumentVersionIndex } from "../useDocumentVersionIndex";
import "./EvidenceCards.css";

interface PdfFactCardProps {
	event: EvidenceEvent;
}

interface DecoratedPdfRef {
	key: string;
	ref: PdfSourceRef;
	href: string;
}

const buildPdfHref = (ref: PdfSourceRef): string => {
	const params = new URLSearchParams();
	if (ref.page != null) params.set("page", String(ref.page));
	// The card already shows the excerpt; passing it as `hl` makes the viewer
	// land on the same words rather than merely the same page.
	if (ref.excerpt) params.set("hl", ref.excerpt);
	const query = params.toString();
	return `doppler://pdf/${ref.file_id}${query ? `?${query}` : ""}`;
};

const decoratePdfRefs = (event: EvidenceEvent): DecoratedPdfRef[] =>
	event.source_refs
		.filter((r): r is PdfSourceRef => r.kind === "pdf")
		.map((ref, index) => ({
			key: `${ref.file_id}-${ref.page ?? ""}-${index}`,
			ref,
			href: buildPdfHref(ref),
		}));

/**
 * Where the cited file sits in its version chain, when the cabinet catalog
 * knows of more than one file in that family. One quiet line — the reader
 * should not have to open the document to learn they are reading last
 * quarter's copy. Absent whenever the catalog has nothing to say.
 */
const VersionNoteLine: React.FC<{ note: DocumentVersionNote }> = ({ note }) => (
	<p className="evidence-card-version-note">
		<span
			className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_CHIP_CLASSES[note.status]}`}
		>
			{note.text}
		</span>
	</p>
);

const PdfFactCard: React.FC<PdfFactCardProps> = ({ event }) => {
	const decorated = useMemo(() => decoratePdfRefs(event), [event]);
	const openDopplerHref = useDopplerUriOpener();
	const versionIndex = useDocumentVersionIndex();
	const rows = useMemo(
		() =>
			decorated.map((row) => ({
				...row,
				versionNote: lookupVersionNote(versionIndex, row.ref.filename),
			})),
		[decorated, versionIndex],
	);

	// One code path with the chat's citation anchors: the in-app viewer when it
	// is mounted, a signed URL in a new tab when it is not.
	const handleOpen = useCallback(
		(href: string, filename: string) => (e: React.MouseEvent) => {
			e.preventDefault();
			openDopplerHref(href, { filename });
		},
		[openDopplerHref],
	);

	return (
		<article className="evidence-card">
			<header className="evidence-card-header">
				<FileText className="evidence-card-header-icon" aria-hidden />
				<div className="evidence-card-header-body">
					<h3 className="evidence-card-title">{event.title}</h3>
					<p className="evidence-card-summary">{event.summary}</p>
				</div>
			</header>
			<ul className="evidence-card-list">
				{rows.map(({ key, ref, href, versionNote }) => (
					<li key={key} className="evidence-card-row">
						<a
							href={href}
							onClick={handleOpen(href, ref.filename)}
							className="evidence-card-row-link"
						>
							<span className="evidence-card-filename">{ref.filename}</span>
							{ref.page != null && (
								<span className="evidence-card-meta">p.{ref.page}</span>
							)}
							{ref.section && (
								<span className="evidence-card-meta">· {ref.section}</span>
							)}
						</a>
						{versionNote && <VersionNoteLine note={versionNote} />}
						{ref.excerpt && (
							<blockquote className="evidence-card-blockquote">
								{ref.excerpt}
							</blockquote>
						)}
					</li>
				))}
			</ul>
		</article>
	);
};

export default React.memo(PdfFactCard);
